import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import matchSchedulingAPI, { isMatchSchedulingAbortError } from '../services/matchSchedulingApi';
import { matchesAPI } from '../services/api';
import ClubSearchSelect from './ClubSearchSelect';
import { publicAssetUrl } from '../utils/publicAssetUrl';

const ROSTER_DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

function labelForDayPref(prefsByDay, dayKey, t) {
  const raw = prefsByDay && typeof prefsByDay === 'object' ? prefsByDay[dayKey] : null;
  const list = Array.isArray(raw)
    ? raw
    : raw != null && String(raw).trim()
      ? [String(raw).trim().toLowerCase()]
      : [];
  const allowed = new Set(['morning', 'noon', 'afternoon', 'evening']);
  const order = ['morning', 'noon', 'afternoon', 'evening'];
  const labels = [...new Set(list.map((x) => String(x || '').toLowerCase()).filter((x) => allowed.has(x)))];
  labels.sort((a, b) => order.indexOf(a) - order.indexOf(b));
  if (!labels.length) return t('phase2.playerAvailability.pref_none');
  return labels.map((id) => t(`phase2.playerAvailability.pref_${id}`)).join(', ');
}

/** Parse calendar day from API (YYYY-MM-DD or ISO start). Uses local noon to avoid TZ drift on date-only fixtures. */
function parseSchedulingCalendarDate(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const y = Number(ymd[1]);
    const mo = Number(ymd[2]) - 1;
    const d = Number(ymd[3]);
    return new Date(y, mo, d, 12, 0, 0, 0);
  }
  const t = new Date(s);
  return Number.isNaN(t.getTime()) ? null : t;
}

function formatSchedulingDateReadable(value) {
  const dt = parseSchedulingCalendarDate(value);
  if (!dt) return value == null || value === '' ? '—' : String(value);
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

/** Compact uppercase day stamp, e.g. SUN, MAY 17, 2026 */
function formatSchedulingDateCapsLine(value) {
  const dt = parseSchedulingCalendarDate(value);
  if (!dt) return value == null || value === '' ? '—' : String(value).toUpperCase();
  return dt
    .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    .toUpperCase();
}

function formatSchedulingTime12h(timeVal) {
  if (timeVal == null || timeVal === '') return '';
  let h;
  let m = 0;
  if (timeVal instanceof Date && !Number.isNaN(timeVal.getTime())) {
    h = timeVal.getHours();
    m = timeVal.getMinutes();
  } else {
    const parts = String(timeVal).split(':');
    h = parseInt(parts[0], 10);
    m = parseInt(parts[1] || '0', 10);
    if (Number.isNaN(h)) return String(timeVal);
  }
  const ref = new Date(2000, 0, 1, h, m, 0, 0);
  return ref.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatKickoffReadable(dateStr, timeVal) {
  const d = formatSchedulingDateReadable(dateStr);
  const t = formatSchedulingTime12h(timeVal);
  return t ? `${d} · ${t}` : d;
}

function fortnightEmptyMessage(code) {
  switch (code) {
    case 'no_common_slots_in_window':
      return 'No overlapping time slot fits everyone inside the play window yet. Add availability or reach out to the other team.';
    case 'no_slots_in_window':
      return 'No schedulable times in this fixture’s window. Check league dates or contact your organiser.';
    case 'roster_incomplete':
      return 'This fixture does not yet have four rostered players for availability matching.';
    case 'need_all_rostered_players_to_mark_availability':
      return 'Every rostered player should add overlapping times in Availability when possible.';
    case 'no_full_overlap_try_nudges':
      return '';
    case 'match_completed':
      return '';
    default:
      return 'Add times in Availability or coordinate with the other team.';
  }
}

function kickoffVoteTally(kv, slotKey) {
  if (!kv?.tallies || !slotKey) return 0;
  const hit = kv.tallies.find((row) => row.slotKey === slotKey);
  return hit ? hit.count : 0;
}

function playerHasKickoffVote(kv, slotKey) {
  if (!slotKey) return false;
  if (Array.isArray(kv?.my_votes) && kv.my_votes.some((v) => v.slotKey === slotKey)) return true;
  return kv?.my_vote?.slotKey === slotKey;
}

function isSlotPeriodBlocked(slot) {
  return !!(slot?.injury_blocked || slot?.holiday_blocked);
}

function slotPeriodPillStyle(slot) {
  if (slot?.holiday_blocked && !slot?.injury_blocked) {
    return { background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' };
  }
  return { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' };
}

function slotPeriodBadgeKey(slot) {
  if (slot?.injury_blocked) return 'phase2.playerAvailability.injurySlotBadge';
  if (slot?.holiday_blocked) return 'phase2.playerAvailability.holidaySlotBadge';
  return null;
}

function slotPeriodCannotVoteKey(slot) {
  if (slot?.injury_blocked) return 'phase2.playerAvailability.injuryCannotVote';
  if (slot?.holiday_blocked) return 'phase2.playerAvailability.holidayCannotVote';
  return null;
}

function slotFitLabel(slot, rosterN, t) {
  const n = rosterN || 4;
  if (slot?.injury_blocked && t) {
    return t('phase2.playerAvailability.injuryUnavailable');
  }
  if (slot?.holiday_blocked && t) {
    return t('phase2.playerAvailability.holidayUnavailable');
  }
  const c = slot?.roster_overlap_count;
  if (Number.isFinite(c)) return `Fits ${Math.min(Math.max(0, c), n)}/${n} players`;
  if (slot && slot.kind === 'three_of_four') return `Fits ${Math.max(0, n - 1)}/${n} players`;
  return `Fits ${n}/${n} players`;
}

/** PUT /matches/:id/details expects HH:MM:SS */
function matchTimeToDetailsApi(matchTimeVal) {
  if (matchTimeVal == null || matchTimeVal === '') return '';
  const s = String(matchTimeVal).trim();
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [h, m] = s.split(':');
    return `${h.padStart(2, '0')}:${m}:00`;
  }
  if (/^\d{2}:\d{2}:\d{2}/.test(s)) return s.slice(0, 8);
  return s.slice(0, 8);
}

/** Full scheduling UX (availability + kickoff votes). Embed inside MatchScheduleModal. */
const MatchSchedulingPanel = ({
  active,
  matchId,
  teamId,
  viewerPlayerId = null,
  onSchedulingUpdated = null,
}) => {
  const { t } = useTranslation();
  const scheduleLoadInFlight = useRef(false);
  const [scheduleState, setScheduleState] = useState(null);
  const [scheduleLoadError, setScheduleLoadError] = useState(null);
  const [schedulingDataLoading, setSchedulingDataLoading] = useState(false);
  const [schedError, setSchedError] = useState(null);
  const [fortnightSuggestions, setFortnightSuggestions] = useState(null);
  const [kickoffVoteBusyKey, setKickoffVoteBusyKey] = useState(null);
  const [venueClub, setVenueClub] = useState('');
  const [venueLocation, setVenueLocation] = useState('');
  const [venueSaving, setVenueSaving] = useState(false);

  const loadFortnightSuggestions = useCallback(async () => {
    if (!matchId || !teamId) return;
    try {
      const data = await matchSchedulingAPI.getAvailabilitySuggestions(matchId, teamId);
      setFortnightSuggestions(data);
    } catch (e) {
      if (!isMatchSchedulingAbortError(e)) {
        setFortnightSuggestions(null);
      }
    }
  }, [matchId, teamId]);

  const loadScheduling = useCallback(async () => {
    if (!matchId || !teamId) return;
    if (scheduleLoadInFlight.current) {
      console.debug('[match-scheduling] skip /state — previous request still in flight (server can be ~20s+)');
      return;
    }
    scheduleLoadInFlight.current = true;
    try {
      setScheduleLoadError(null);
      const data = await matchSchedulingAPI.getState(matchId, teamId);
      setScheduleState(data);
      setSchedError(null);
    } catch (e) {
      if (isMatchSchedulingAbortError(e)) {
        console.debug('[match-scheduling] /state aborted — not overwriting schedule UI');
        return;
      }
      setScheduleLoadError('Could not load match scheduling');
    } finally {
      scheduleLoadInFlight.current = false;
    }
  }, [matchId, teamId]);

  useEffect(() => {
    if (!active || !matchId || !teamId) {
      setScheduleState(null);
      setScheduleLoadError(null);
      setFortnightSuggestions(null);
      setSchedError(null);
      setSchedulingDataLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSchedulingDataLoading(true);
    (async () => {
      try {
        await Promise.all([loadScheduling(), loadFortnightSuggestions()]);
      } finally {
        if (!cancelled) setSchedulingDataLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, matchId, teamId, loadScheduling, loadFortnightSuggestions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- sync venue when server-sourced fields change (narrow deps)
  useEffect(() => {
    const lm = scheduleState?.leagueMatch;
    if (!lm) return;
    setVenueClub(String(lm.court_name || lm.venue || '').trim());
    setVenueLocation(String(lm.court_location || '').trim());
  }, [
    scheduleState?.leagueMatch?.id,
    scheduleState?.leagueMatch?.court_name,
    scheduleState?.leagueMatch?.venue,
    scheduleState?.leagueMatch?.court_location,
  ]);

  useEffect(() => {
    if (!active || !matchId || !teamId) return undefined;
    const id = setInterval(() => loadScheduling(), 45000);
    return () => clearInterval(id);
  }, [active, matchId, teamId, loadScheduling]);

  const handleKickoffVote = async (dateStr, timeStr, slotKey, injuryBlocked = false) => {
    if (!teamId || !viewerPlayerId || !matchId || injuryBlocked) return;
    setKickoffVoteBusyKey(slotKey);
    setSchedError(null);
    try {
      const data = await matchSchedulingAPI.confirmAvailabilitySlot(matchId, teamId, dateStr, timeStr);
      if (data.success === false) {
        setSchedError(data.message || 'Could not record vote');
        return;
      }
      if (data.committed) {
        toast.success(t('phase2.playerAvailability.kickoffToastLocked'));
      } else if (data.action === 'removed') {
        toast.success(t('phase2.playerAvailability.kickoffToastVoteRemoved'));
      } else {
        toast.success(
          data.message || t('phase2.playerAvailability.kickoffToastProgressFallback')
        );
      }
      await loadScheduling();
      await loadFortnightSuggestions();
      onSchedulingUpdated?.();
    } catch (err) {
      setSchedError(err.response?.data?.message || err.message || 'Could not save vote');
    } finally {
      setKickoffVoteBusyKey(null);
    }
  };

  const handleSaveVenueMini = async () => {
    const lm = scheduleState?.leagueMatch;
    if (!teamId || !matchId || !lm?.match_date || !lm?.match_time) return;
    if (!venueClub.trim()) {
      toast.error('Enter club or venue name.');
      return;
    }
    setVenueSaving(true);
    setSchedError(null);
    try {
      const rawMd = lm.match_date;
      let ymd =
        typeof rawMd === 'string' && /^\d{4}-\d{2}-\d{2}/.test(rawMd) ? rawMd.slice(0, 10) : null;
      if (!ymd) {
        const d = parseSchedulingCalendarDate(rawMd);
        if (d) {
          ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(
            2,
            '0'
          )}`;
        }
      }
      if (!ymd) {
        toast.error('Could not read match date.');
        return;
      }

      const result = await matchesAPI.updateMatchDetails(
        matchId,
        {
          court_name: venueClub.trim(),
          court_location: venueLocation.trim() || null,
          match_date: ymd,
          match_time: matchTimeToDetailsApi(lm.match_time),
          venue: venueClub.trim(),
        },
        teamId
      );
      if (result?.success !== false && !result?.error) {
        toast.success('Venue saved.');
        await loadScheduling();
        onSchedulingUpdated?.();
      } else {
        toast.error(result?.message || result?.error || 'Could not save venue');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Could not save venue');
    } finally {
      setVenueSaving(false);
    }
  };

  if (!active) return null;

  if (schedulingDataLoading && !scheduleLoadError) {
    return (
      <SchedulingRoot aria-busy="true">
        <VisuallyHiddenSpan>Loading schedule…</VisuallyHiddenSpan>
        <SchedulePanelSkeleton />
      </SchedulingRoot>
    );
  }

  return (
    <SchedulingRoot>
      {schedError && <PanelError role="alert">{schedError}</PanelError>}
      {scheduleState?.success &&
        scheduleState.leagueMatch &&
        String(scheduleState.leagueMatch.status || '').toLowerCase() === 'scheduled' &&
        (() => {
          const lmSchedule = scheduleState.leagueMatch;
          const fs = fortnightSuggestions;
          const kv = fs?.kickoff_voting;
          const hasKickoff = !!(lmSchedule.match_date && lmSchedule.match_time);
          const fortnightActive =
            fs?.success &&
            !fs.disabled &&
            fs.emptyReason !== 'match_already_scheduled_or_not_scheduled' &&
            !hasKickoff;

          const prefSortHintActive =
            fs?.soft_preferences_active &&
            Array.isArray(fs.goldSlots) &&
            fs.goldSlots.length > 1 &&
            fs.goldSlots.some((x) => x.preference_aligned) &&
            fs.goldSlots.some((x) => !x.preference_aligned);

          const rosterOkUi =
            viewerPlayerId &&
            Array.isArray(scheduleState.roster_player_ids) &&
            scheduleState.roster_player_ids.map(String).includes(String(viewerPlayerId));

          const homeNmSchedule =
            lmSchedule.home_team_id && String(lmSchedule.home_team_id) === String(lmSchedule.team1_id)
              ? lmSchedule.team1_name
              : lmSchedule.team2_name;
          const awayNmSchedule =
            lmSchedule.home_team_id && String(lmSchedule.home_team_id) === String(lmSchedule.team1_id)
              ? lmSchedule.team2_name
              : lmSchedule.team1_name;

          const rosterOverview = fs?.roster_overview;
          const teamsOverview = Array.isArray(rosterOverview?.teams) ? rosterOverview.teams : [];
          const viewerRosterTeam =
            teamsOverview.length && teamId
              ? teamsOverview.find((tm) => String(tm.team_id) === String(teamId))
              : null;

          let duelLeft = null;
          let duelRight = null;
          let duelHintLeft = null;
          let duelHintRight = null;
          if (teamsOverview.length >= 2) {
            if (teamId) {
              const mine = teamsOverview.find((tm) => String(tm.team_id) === String(teamId));
              const opp = teamsOverview.find((tm) => String(tm.team_id) !== String(mine?.team_id));
              if (mine && opp) {
                duelLeft = mine;
                duelRight = opp;
                duelHintLeft = t('phase2.playerAvailability.yourRosterAvailability');
                duelHintRight = t('phase2.playerAvailability.opponentRosterAvailability');
              }
            }
            if (!duelLeft || !duelRight) {
              duelLeft = teamsOverview[0];
              duelRight = teamsOverview[1];
              duelHintLeft = t('phase2.playerAvailability.rosterAvailabilityNeutral');
              duelHintRight = t('phase2.playerAvailability.rosterAvailabilityNeutral');
            }
          }

          const rosterPlayersWithoutViewer = (teamRow) => {
            if (!teamRow || !Array.isArray(teamRow.players)) return [];
            if (!viewerPlayerId) return teamRow.players;
            return teamRow.players.filter((p) => String(p.player_id) !== String(viewerPlayerId));
          };
          const teammateRosterTeam =
            duelLeft && teamId && String(duelLeft.team_id) === String(teamId)
              ? { ...duelLeft, players: rosterPlayersWithoutViewer(duelLeft) }
              : duelLeft && duelRight && teamId && String(duelRight.team_id) === String(teamId)
                ? { ...duelRight, players: rosterPlayersWithoutViewer(duelRight) }
                : null;
          const opponentRosterTeam =
            duelLeft && duelRight && teamId
              ? String(duelLeft.team_id) === String(teamId)
                ? duelRight
                : duelLeft
              : duelRight || duelLeft;
          const showTeammatePanel = (teammateRosterTeam?.players?.length ?? 0) > 0;
          const showOpponentPanel = (opponentRosterTeam?.players?.length ?? 0) > 0;
          const filteredViewerTeam = viewerRosterTeam
            ? { ...viewerRosterTeam, players: rosterPlayersWithoutViewer(viewerRosterTeam) }
            : null;

          const scheduleTopBar =
            teamId && (showTeammatePanel || showOpponentPanel) ? (
              <>
                {scheduleState.my_team_is_home != null && rosterOkUi && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <ScheduleTag $home={!!scheduleState.my_team_is_home}>
                      {scheduleState.my_team_is_home
                        ? 'Your team is home — book the court.'
                        : 'Your team is away.'}
                    </ScheduleTag>
                  </div>
                )}
                <DualRosterGrid $single={!showTeammatePanel || !showOpponentPanel}>
                  {showTeammatePanel ? (
                    <ViewerTeamRosterBlock
                      team={teammateRosterTeam}
                      t={t}
                      availabilityHint={t('phase2.playerAvailability.teammateRosterAvailability')}
                    />
                  ) : null}
                  {showOpponentPanel ? (
                    <ViewerTeamRosterBlock
                      team={opponentRosterTeam}
                      t={t}
                      availabilityHint={duelHintRight || t('phase2.playerAvailability.opponentRosterAvailability')}
                    />
                  ) : null}
                </DualRosterGrid>
              </>
            ) : duelLeft && duelRight ? (
              <>
                {scheduleState.my_team_is_home != null && rosterOkUi && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <ScheduleTag $home={!!scheduleState.my_team_is_home}>
                      {scheduleState.my_team_is_home
                        ? 'Your team is home — book the court.'
                        : 'Your team is away.'}
                    </ScheduleTag>
                  </div>
                )}
                <DualRosterGrid>
                  <ViewerTeamRosterBlock team={duelLeft} t={t} availabilityHint={duelHintLeft} />
                  <ViewerTeamRosterBlock team={duelRight} t={t} availabilityHint={duelHintRight} />
                </DualRosterGrid>
              </>
            ) : filteredViewerTeam?.players?.length > 0 ? (
              <>
                {scheduleState.my_team_is_home != null && rosterOkUi && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <ScheduleTag $home={!!scheduleState.my_team_is_home}>
                      {scheduleState.my_team_is_home
                        ? 'Your team is home — book the court.'
                        : 'Your team is away.'}
                    </ScheduleTag>
                  </div>
                )}
                <ViewerTeamRosterBlock
                  team={filteredViewerTeam}
                  t={t}
                  availabilityHint={t('phase2.playerAvailability.teammateRosterAvailability')}
                />
              </>
            ) : (
            <HomeAwayStrip>
              <span>
                <strong>Home</strong> {homeNmSchedule || 'TBD'}
              </span>
              <HomeAwaySep>vs</HomeAwaySep>
              <span>
                <strong>Away</strong> {awayNmSchedule || 'TBD'}
              </span>
              {scheduleState.my_team_is_home != null && rosterOkUi && (
                <ScheduleTag $home={!!scheduleState.my_team_is_home}>
                  {scheduleState.my_team_is_home
                    ? 'Your team is home — book the court.'
                    : 'Your team is away.'}
                </ScheduleTag>
              )}
            </HomeAwayStrip>
          );

          const canEditVenueMini = rosterOkUi && scheduleState.my_team_is_home !== false;

          const schedulePanelInner = (
            <>
              {hasKickoff && (
                <>
                  <DrawerSectionTitle>Confirm where you play</DrawerSectionTitle>
                  <KickoffLead style={{ marginTop: '0.35rem', marginBottom: '0.5rem' }}>
                    {formatKickoffReadable(lmSchedule.match_date, lmSchedule.match_time)}
                  </KickoffLead>
                  {!canEditVenueMini && rosterOkUi && scheduleState.my_team_is_home === false ? (
                    <DrawerHint>
                      Only the <strong>home</strong> team enters venue details in this scheduling window. Use{' '}
                      <strong>Match details</strong> on your match card if you need to coordinate or suggest updates.
                    </DrawerHint>
                  ) : (
                    <DrawerHint>Add venue so both teams see the same court / club listing.</DrawerHint>
                  )}
                  <VenueMiniForm role="group" aria-label="Venue details">
                    <VenueMiniLabel htmlFor={`msched-venue-${matchId}`}>Club / venue name *</VenueMiniLabel>
                    {canEditVenueMini ? (
                      <ClubSearchSelect
                        id={`msched-venue-${matchId}`}
                        value={venueClub}
                        onChange={setVenueClub}
                        placeholder="Search and select a club…"
                        required
                      />
                    ) : (
                      <VenueMiniInput
                        id={`msched-venue-${matchId}`}
                        value={venueClub}
                        readOnly
                        style={{
                          opacity: 0.88,
                          background: '#f1f5f9',
                        }}
                      />
                    )}
                    <VenueMiniLabel htmlFor={`msched-loc-${matchId}`}>
                      Court or extra detail (optional)
                    </VenueMiniLabel>
                    <VenueMiniInput
                      id={`msched-loc-${matchId}`}
                      value={venueLocation}
                      onChange={(e) => setVenueLocation(e.target.value)}
                      placeholder="Court 3 · indoor south"
                      readOnly={!canEditVenueMini}
                      style={{
                        opacity: canEditVenueMini ? 1 : 0.88,
                        background: canEditVenueMini ? '#fff' : '#f1f5f9',
                      }}
                    />
                    {rosterOkUi && canEditVenueMini ? (
                      <VenueMiniSave
                        type="button"
                        disabled={venueSaving || !venueClub.trim()}
                        onClick={handleSaveVenueMini}
                      >
                        {venueSaving ? 'Saving…' : 'Save venue details'}
                      </VenueMiniSave>
                    ) : rosterOkUi && !canEditVenueMini ? (
                      <DrawerHint style={{ marginBottom: 0 }}>
                        Home team can save venue fields here after game time is confirmed.
                      </DrawerHint>
                    ) : (
                      <DrawerHint>Sign in as a rostered player to save venue fields.</DrawerHint>
                    )}
                  </VenueMiniForm>
                </>
              )}

              {!hasKickoff && fortnightActive && (
                <>
                  {kv && kv.roster_count > 0 && (
                    <VoteProgressBanner role="status">
                      {(() => {
                        const leading = kv.tallies && kv.tallies.length ? kv.tallies[0] : null;
                        if (leading && leading.count > 0) {
                          return t('phase2.playerAvailability.kickoffVoteLeading', {
                            count: leading.count,
                            roster: kv.roster_count,
                            slot: formatKickoffReadable(leading.date, leading.time),
                          });
                        }
                        return t('phase2.playerAvailability.kickoffVoteNone');
                      })()}
                    </VoteProgressBanner>
                  )}
                  {(() => {
                    const gold = Array.isArray(fs.goldSlots) ? fs.goldSlots : [];
                    const nudges = Array.isArray(fs.nudges) ? fs.nudges : [];
                    const voteableGold = gold.filter((s) => !isSlotPeriodBlocked(s));
                    const blockedGold = gold.filter((s) => isSlotPeriodBlocked(s));
                    const primary = voteableGold[0] || null;
                    const carouselSlots = [...voteableGold.slice(1), ...blockedGold, ...nudges];
                    const rosterNc = fs?.roster_count || kv?.roster_count || 4;
                    const kickoffVotesNeed =
                      kv?.votes_required && kv.votes_required > 0 ? kv.votes_required : rosterNc;
                    const primaryVc = primary ? kickoffVoteTally(kv, primary.slotKey) : 0;
                    const primaryMine = primary && playerHasKickoffVote(kv, primary.slotKey);
                    const showOpportunities = !!(primary || carouselSlots.length > 0);

                    return (
                      <>
                        {showOpportunities ? (
                          <>
                            <OpportunitiesHeader>
                              <OpportunitiesTitle>
                                {t('phase2.playerAvailability.bestMatchOpportunities')}
                              </OpportunitiesTitle>
                              <SystemAnalysisRow>
                                <SystemAnalysisLabel>
                                  {t('phase2.playerAvailability.systemAnalysis')}:
                                </SystemAnalysisLabel>
                                <ReliabilityChip>
                                  {t('phase2.playerAvailability.highReliability')}
                                </ReliabilityChip>
                              </SystemAnalysisRow>
                            </OpportunitiesHeader>

                            {primary ? (
                              <HeroSlotCard $best={!!primary.preference_aligned && !isSlotPeriodBlocked(primary)}>
                                {isSlotPeriodBlocked(primary) ? (
                                  <HeroSlotPillRow>
                                    <PreferredPill style={slotPeriodPillStyle(primary)}>
                                      {t(slotPeriodBadgeKey(primary))}
                                    </PreferredPill>
                                  </HeroSlotPillRow>
                                ) : primary.preference_aligned ? (
                                  <HeroSlotPillRow>
                                    <PreferredPill>
                                      {t('phase2.playerAvailability.preferenceBadge')}
                                    </PreferredPill>
                                  </HeroSlotPillRow>
                                ) : null}
                                <HeroSlotEyebrow>{formatSchedulingDateCapsLine(primary.date)}</HeroSlotEyebrow>
                                <HeroSlotTime>{formatKickoffReadable(primary.date, primary.time)}</HeroSlotTime>
                                <HeroSlotMeta>
                                  {slotFitLabel(primary, rosterNc, t)} ·{' '}
                                  {t('phase2.playerAvailability.kickoffSlotPickCount', {
                                    count: primaryVc,
                                    need: kickoffVotesNeed,
                                  })}
                                </HeroSlotMeta>
                                {rosterOkUi && viewerPlayerId ? (
                                  <HeroConfirmBtn
                                    type="button"
                                    disabled={
                                      kickoffVoteBusyKey === primary.slotKey || isSlotPeriodBlocked(primary)
                                    }
                                    onClick={() =>
                                      handleKickoffVote(
                                        primary.date,
                                        primary.time,
                                        primary.slotKey,
                                        isSlotPeriodBlocked(primary)
                                      )
                                    }
                                  >
                                    {kickoffVoteBusyKey === primary.slotKey
                                      ? '…'
                                      : primaryMine
                                        ? 'Your vote ✓'
                                        : 'Confirm slot'}
                                  </HeroConfirmBtn>
                                ) : (
                                  <DrawerHint>Roster sign-in required to vote.</DrawerHint>
                                )}
                              </HeroSlotCard>
                            ) : (
                              <DrawerHint style={{ marginBottom: 8, marginTop: 0 }}>
                                No slot fits all {rosterNc} players yet — browse below or add times in{' '}
                                <Link to="/player/settings/availability">Availability</Link>.
                              </DrawerHint>
                            )}

                            <MatchSlotCarousel
                              slots={carouselSlots}
                              rosterN={rosterNc}
                              kickoffVotesNeed={kickoffVotesNeed}
                              kv={kv}
                              rosterOkUi={rosterOkUi}
                              viewerPlayerId={viewerPlayerId}
                              kickoffVoteBusyKey={kickoffVoteBusyKey}
                              onVote={handleKickoffVote}
                              t={t}
                              carouselSubtitle={t('phase2.playerAvailability.moreSchedulingOptions')}
                            />

                            <SchedulingDetailsHint>
                              <details>
                                <summary>{t('phase2.playerAvailability.schedulingTipsSummary')}</summary>
                                {prefSortHintActive ? (
                                  <DrawerHint style={{ marginTop: 8, marginBottom: 6 }}>
                                    {t('phase2.playerAvailability.schedulingPrefSortHint')}
                                  </DrawerHint>
                                ) : null}
                                <DrawerHint style={{ marginBottom: 0 }}>
                                  Use{' '}
                                  <Link to="/player/settings/availability">Availability</Link> so “Fits n/4” reflects
                                  your real calendar when you can.
                                </DrawerHint>
                              </details>
                            </SchedulingDetailsHint>
                          </>
                        ) : null}

                        {!showOpportunities && fortnightEmptyMessage(fs.emptyReason) ? (
                          <DrawerHint>{fortnightEmptyMessage(fs.emptyReason)}</DrawerHint>
                        ) : null}
                      </>
                    );
                  })()}
                </>
              )}

              {!hasKickoff && !fortnightActive && (
                <>
                  <SchedulingRow style={{ marginTop: '10px' }}>
                    <strong>Home ({homeNmSchedule || 'TBD'}):</strong> books the court off-app, then enters venue and
                    court info under Match details when you agree a time elsewhere.
                  </SchedulingRow>
                  {scheduleState.my_team_is_home && (
                    <SchedulingHomeTag>Your team is home — you handle the booking.</SchedulingHomeTag>
                  )}
                  {scheduleState.booking_system_url && (
                    <BookingLinkWrap>
                      <a href={scheduleState.booking_system_url} target="_blank" rel="noopener noreferrer">
                        Open club booking website
                      </a>
                    </BookingLinkWrap>
                  )}
                  <DrawerHint style={{ marginTop: '10px', marginBottom: 0 }}>
                    Add overlapping availability slots in{' '}
                    <Link to="/player/settings/availability">Availability</Link> when you can — or arrange a time directly
                    with your opponents / league admin if needed.
                  </DrawerHint>
                </>
              )}
            </>
          );

          return (
            <>
              {scheduleTopBar}
              <SchedulePanelCard role="region" aria-label="Availability and game time">
                <RosterAvailabilityBoard
                  board={fs?.availability_board}
                  viewerPlayerId={viewerPlayerId}
                  rosterOkUi={rosterOkUi}
                  kickoffVoteBusyKey={kickoffVoteBusyKey}
                  kickoffVoting={kv}
                  onVote={handleKickoffVote}
                  t={t}
                />
                {schedulePanelInner}
              </SchedulePanelCard>
            </>
          );
        })()}
      {scheduleLoadError && <SchedulingMuted>{scheduleLoadError}</SchedulingMuted>}
      {!scheduleLoadError &&
        scheduleState?.success &&
        scheduleState.leagueMatch &&
        String(scheduleState.leagueMatch.status || '').toLowerCase() === 'completed' && (
          <SchedulingMuted>{t('phase2.playerAvailability.matchScheduleCompleted')}</SchedulingMuted>
        )}
      {!scheduleLoadError &&
        scheduleState?.success &&
        scheduleState.disabled &&
        scheduleState.leagueMatch &&
        String(scheduleState.leagueMatch.status || '').toLowerCase() !== 'completed' && (
          <SchedulingMuted>{t('phase2.playerAvailability.schedulingTenantDisabled')}</SchedulingMuted>
        )}
      {!scheduleLoadError &&
        scheduleState?.success &&
        scheduleState.leagueMatch &&
        String(scheduleState.leagueMatch.status || '').toLowerCase() !== 'scheduled' &&
        String(scheduleState.leagueMatch.status || '').toLowerCase() !== 'completed' && (
          <SchedulingMuted>
            Schedule pickers here apply to fixtures that are waiting to be arranged. Ask your league if this doesn’t match
            what you see elsewhere.
          </SchedulingMuted>
        )}
    </SchedulingRoot>
  );
};

const schedSkeletonShimmer = keyframes`
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
`;

function SchedulePanelSkeleton() {
  return (
    <ScheduleSkeletonCard role="presentation" aria-hidden>
      <SkeletonHomeStrip>
        <SkeletonBar $w="42%" />
        <SkeletonBar $w="48px" $mb={0} />
        <SkeletonBar $w="38%" />
        <SkeletonChip />
      </SkeletonHomeStrip>

      <SkeletonBar $w="92%" />
      <SkeletonBar $w="76%" />

      <SkeletonHeading />
      <SkeletonVoteBanner />

      <SkeletonSlotRow />
      <SkeletonSlotRow />
      <SkeletonSlotRow />

      <SkeletonHeading $dim />
      <SkeletonBar $w="58%" />

      <SkeletonSlotRow $compact />
      <SkeletonSlotRow $compact />

      <SkeletonBar $w="88%" $mt={6} />
    </ScheduleSkeletonCard>
  );
}

const ScheduleSkeletonCard = styled.div`
  padding: 12px 12px 14px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
`;

const skeletonPulse = css`
  background: linear-gradient(90deg, #e8edf3 0%, #f6f8fb 42%, #e8edf3 88%);
  background-size: 220% 100%;
  animation: ${schedSkeletonShimmer} 1.15s ease-in-out infinite;
`;

const SkeletonHomeStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: linear-gradient(90deg, #eff6ff 0%, #fff7ed 100%);
`;

const SkeletonBar = styled.div`
  height: ${(p) => (p.$thin ? '8px' : '13px')};
  margin-bottom: ${(p) => (p.$mb === undefined ? '10px' : `${p.$mb}px`)};
  margin-top: ${(p) => (p.$mt != null ? `${p.$mt}px` : undefined)};
  width: ${(p) => p.$w || '100%'};
  max-width: 100%;
  border-radius: 8px;
  ${skeletonPulse};
`;

const SkeletonChip = styled.div`
  margin-left: auto;
  height: 22px;
  width: 120px;
  border-radius: 999px;
  ${skeletonPulse};
`;

const SkeletonHeading = styled.div`
  width: 46%;
  max-width: 200px;
  height: 10px;
  border-radius: 4px;
  margin-bottom: 10px;
  opacity: ${(p) => (p.$dim ? 0.55 : 0.72)};
  ${skeletonPulse};
`;

const SkeletonVoteBanner = styled.div`
  height: 42px;
  border-radius: 8px;
  margin-bottom: 12px;
  border-left: 3px solid #cbd5e1;
  ${skeletonPulse};
`;

const SkeletonSlotRow = styled.div`
  height: ${(p) => (p.$compact ? '42px' : '50px')};
  border-radius: 10px;
  margin-bottom: 8px;
  border: 1px solid #e8edf3;
  ${skeletonPulse};
`;

const VisuallyHiddenSpan = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const PanelError = styled.div`
  margin: 0 0 12px 0;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #991b1b;
  font-size: 13px;
  line-height: 1.4;
`;

const SchedulingRoot = styled.div`
  padding: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #1f2937;
`;

const SchedulePanelCard = styled.div`
  margin-bottom: 12px;
  padding: 12px 12px 10px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
`;

const HomeAwayStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: linear-gradient(90deg, #eff6ff 0%, #fff7ed 100%);
  font-size: 12px;
  color: #1e293b;
`;

const HomeAwaySep = styled.span`
  color: #94a3b8;
  font-weight: 600;
  font-size: 11px;
`;

const ScheduleTag = styled.span`
  margin-left: auto;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${(p) => (p.$home ? '#dcfce7' : '#fef3c7')};
  color: ${(p) => (p.$home ? '#166534' : '#92400e')};
`;

const DrawerSectionTitle = styled.h3`
  margin: 0 0 6px 0;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
`;

const DrawerHint = styled.p`
  margin: 0 0 10px 0;
  font-size: 11px;
  line-height: 1.45;
  color: #64748b;
  display: ${(p) => (p.$inline ? 'inline' : 'block')};

  a {
    font-weight: 700;
    color: #043663;
  }
`;

const VoteProgressBanner = styled.div`
  font-size: 11px;
  line-height: 1.4;
  color: #334155;
  background: #eef6fc;
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 10px;
  border: 1px solid #bfdbfe;
  border-left: 3px solid #3b82f6;
`;

const PreferredPill = styled.span`
  display: inline-block;
  align-self: flex-start;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #065f46;
  background: #d1fae5;
  border-radius: 999px;
  padding: 3px 8px;
  margin-bottom: 4px;
`;

const SlotOptionCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px;
  margin-bottom: 8px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$soft ? '#93c5fd' : '#dbeafe')};
  background: ${(p) => (p.$soft ? '#eff6ff' : '#f8fafc')};
`;

const SlotOptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  strong {
    font-size: 13px;
    color: #0f172a;
  }

  span {
    font-size: 11px;
    color: #64748b;
  }
`;

const VoteKickoffBtn = styled.button`
  border: none;
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  background: ${(p) => (p.$soft ? '#0f766e' : '#043663')};
  color: #fff;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }
`;

const VenueMiniForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
`;

const VenueMiniLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: #475569;
`;

const VenueMiniInput = styled.input`
  font-size: 13px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;

  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 2px rgba(4, 54, 99, 0.12);
  }
`;

const VenueMiniSave = styled.button`
  margin-top: 6px;
  align-self: flex-start;
  background: #043663;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const NudgeBlock = styled.div`
  margin-top: 6px;
  margin-bottom: 6px;
`;

const SchedulingMuted = styled.p`
  margin: 0 0 8px 0;
  color: #6b7280;
  font-size: 12px;
`;

const SchedulingRow = styled.div`
  margin-bottom: 8px;
`;

const SchedulingHomeTag = styled.div`
  display: inline-block;
  margin-top: 6px;
  margin-bottom: 8px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 12px;
  font-weight: 600;
`;

const BookingLinkWrap = styled.div`
  margin: 8px 0 10px;
  a {
    color: #043663;
    font-weight: 600;
  }
`;

const ViewerRosterShell = styled.div`
  margin-bottom: 12px;
  padding: 12px 12px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fafafa;
`;

const DualRosterGrid = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$single ? '1fr' : '1fr 1fr')};
  gap: 12px;
  margin-bottom: 12px;
  align-items: start;

  & > ${ViewerRosterShell} {
    margin-bottom: 0;
    min-width: 0;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ViewerRosterHeading = styled.div`
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #0f172a;
  margin-bottom: 2px;
  line-height: 1.25;
  word-break: break-word;
`;

const ViewerRosterHint = styled.p`
  margin: 0 0 10px 0;
  font-size: 11px;
  line-height: 1.35;
  color: #64748b;
`;

const ViewerMemberGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const ViewerMemberCard = styled.div`
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 8px 8px 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
`;

const ViewerMemberTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const ViewerAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid #e2e8f0;
`;

const ViewerAvatarPh = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  color: #475569;
  background: #e2e8f0;
  border: 2px solid #cbd5e1;
`;

const ViewerMemberName = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.25;
  word-break: break-word;
`;

const RosterDayList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const RosterDayRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.4fr);
  gap: 4px;
  font-size: 9px;
  line-height: 1.3;
  padding: 2px 0;
  border-top: 1px solid #f1f5f9;

  &:first-child {
    border-top: none;
    padding-top: 0;
  }
`;

const RosterDayShort = styled.span`
  color: #64748b;
  font-weight: 600;
`;

const RosterDayValue = styled.span`
  color: #0f172a;
  font-weight: 500;
`;

const HeroSlotCard = styled.div`
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$best ? '#d52b1e' : '#bfdbfe')};
  box-shadow: ${(p) =>
    p.$best ? '0 4px 16px rgba(213, 43, 30, 0.1)' : '0 2px 10px rgba(4, 54, 99, 0.07)'};
  background: linear-gradient(180deg, #f0f7ff 0%, #f8fafc 100%);
  padding: 12px 12px 10px;
  margin-bottom: 12px;
`;

const HeroSlotPillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

const HeroSlotEyebrow = styled.div`
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 4px;
`;

const HeroSlotTime = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 3px;
  line-height: 1.2;
`;

const HeroSlotMeta = styled.div`
  font-size: 10px;
  color: #64748b;
  margin-bottom: 8px;
  letter-spacing: 0.01em;
`;

const HeroConfirmBtn = styled.button`
  width: 100%;
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  background: #d52b1e;
  color: #fff;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }
`;

const CarouselSection = styled.div`
  margin-top: 2px;
  margin-bottom: 8px;
`;

const ReliabilityChip = styled.span`
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid #86efac;
  color: #14532d;
  background: #f0fdf4;
`;

const OpportunitiesHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
`;

const OpportunitiesTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #0f1d36;
`;

const SystemAnalysisRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const SystemAnalysisLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
`;

const SchedulingDetailsHint = styled.div`
  margin-top: 8px;
  font-size: 11px;
  color: #64748b;

  summary {
    cursor: pointer;
    font-weight: 700;
    color: #475569;
    list-style: none;
    outline: none;
  }

  summary::-webkit-details-marker {
    display: none;
  }
`;

const CarouselShell = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 6px;
`;

const CarouselArrow = styled.button`
  flex-shrink: 0;
  width: 34px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #0f172a;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);

  &:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
  }

  @media (max-width: 520px) {
    width: 30px;
    font-size: 14px;
  }
`;

const CarouselTrack = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  padding: 4px 2px 10px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
  }
`;

const CarouselCard = styled.div`
  flex: 0 0 min(220px, 78vw);
  scroll-snap-align: start;
  border-radius: 12px;
  border: 1px solid #bfdbfe;
  background: linear-gradient(180deg, #f0f7ff 0%, #f8fafc 100%);
  padding: 10px 10px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  box-shadow: 0 2px 10px rgba(4, 54, 99, 0.06);
`;

const CarouselCardDate = styled.div`
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #64748b;
`;

const CarouselCardTime = styled.div`
  font-size: 0.94rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
`;

const CarouselCardMeta = styled.div`
  font-size: 9px;
  color: #64748b;
  margin-bottom: 2px;
`;

const CarouselCardVote = styled.button`
  margin-top: auto;
  border-radius: 999px;
  border: 1px solid #0f172a;
  background: #fff;
  color: #0f172a;
  padding: 8px 12px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #0f172a;
    color: #fff;
  }
`;

const CarouselSubtitle = styled.div`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 6px;
`;

const KickoffLead = styled.div`
  margin-top: 4px;
  font-size: 1.0625rem;
  line-height: 1.4;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #0f172a;
`;

const BoardShell = styled.section`
  margin: 0 0 14px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
`;

const BoardTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
`;

const BoardHint = styled.p`
  margin: 4px 0 10px;
  font-size: 0.78rem;
  color: #64748b;
`;

const BoardPlayers = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
`;

const BoardPlayer = styled.div`
  min-width: 120px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
`;

const BoardPlayerRole = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
`;

const BoardPlayerName = styled.div`
  font-size: 0.84rem;
  font-weight: 700;
  color: #0f172a;
`;

const BoardPlayerMeta = styled.div`
  font-size: 11px;
  color: #64748b;
`;

const BoardSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  margin-top: 8px;
  border-radius: 10px;
  background: ${(p) => (p.$full ? '#ecfdf5' : '#fff')};
  border: 1px solid ${(p) => (p.$full ? '#86efac' : '#e2e8f0')};
`;

const BoardSlotTime = styled.div`
  min-width: 168px;
  font-size: 0.84rem;
  font-weight: 700;
  color: #0f172a;
`;

const BoardDots = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
`;

const BoardDot = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 800;
  color: ${(p) => (p.$free ? '#14532d' : '#94a3b8')};
  background: ${(p) => (p.$free ? '#bbf7d0' : '#f1f5f9')};
  border: 1px solid ${(p) => (p.$free ? '#4ade80' : '#e2e8f0')};
`;

const BoardPropose = styled.button`
  border: 0;
  border-radius: 999px;
  background: #043663;
  color: #fff;
  padding: 7px 12px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

function orderAvailabilityPlayers(players, viewerPlayerId) {
  const list = Array.isArray(players) ? players : [];
  const you = list.find((p) => viewerPlayerId && String(p.player_id) === String(viewerPlayerId));
  const teammates = list.filter(
    (p) => you && p !== you && p.team_id && you.team_id && String(p.team_id) === String(you.team_id)
  );
  const opponents = list.filter((p) => p !== you && !teammates.includes(p));
  return [...(you ? [you] : []), ...teammates, ...opponents];
}

function availabilityRole(player, viewerPlayerId, youTeamId, t) {
  if (viewerPlayerId && String(player.player_id) === String(viewerPlayerId)) {
    return t('phase2.playerAvailability.availabilityBoardYou');
  }
  if (youTeamId && player.team_id && String(player.team_id) === String(youTeamId)) {
    return t('phase2.playerAvailability.availabilityBoardTeammate');
  }
  return t('phase2.playerAvailability.availabilityBoardOpponent');
}

function RosterAvailabilityBoard({
  board,
  viewerPlayerId,
  rosterOkUi,
  kickoffVoteBusyKey,
  kickoffVoting,
  onVote,
  t,
}) {
  if (!board || !Array.isArray(board.players) || board.players.length === 0) return null;
  const players = orderAvailabilityPlayers(board.players, viewerPlayerId);
  const you = players.find((p) => viewerPlayerId && String(p.player_id) === String(viewerPlayerId));
  const slots = Array.isArray(board.slots) ? board.slots : [];
  const total = players.length;

  return (
    <BoardShell>
      <BoardTitle>{t('phase2.playerAvailability.availabilityBoardTitle')}</BoardTitle>
      <BoardHint>{t('phase2.playerAvailability.availabilityBoardHint')}</BoardHint>
      <BoardPlayers>
        {players.map((p) => (
          <BoardPlayer key={p.player_id}>
            <BoardPlayerRole>{availabilityRole(p, viewerPlayerId, you?.team_id, t)}</BoardPlayerRole>
            <BoardPlayerName>{p.name}</BoardPlayerName>
            <BoardPlayerMeta>
              {p.hours_marked > 0
                ? t('phase2.playerAvailability.availabilityBoardHours', { count: p.hours_marked })
                : t('phase2.playerAvailability.availabilityBoardNoHours')}
            </BoardPlayerMeta>
          </BoardPlayer>
        ))}
      </BoardPlayers>
      {slots.length === 0 ? (
        <BoardHint style={{ marginBottom: 0 }}>
          {t('phase2.playerAvailability.availabilityBoardEmpty')}{' '}
          <Link to="/player/settings/availability">Availability</Link>
        </BoardHint>
      ) : (
        slots.map((slot) => {
          const free = new Set((slot.free_player_ids || []).map(String));
          const full = free.size >= total && total > 0;
          const voted = playerHasKickoffVote(kickoffVoting, slot.slotKey);
          const busy = kickoffVoteBusyKey === slot.slotKey;
          return (
            <BoardSlot key={slot.slotKey} $full={full}>
              <BoardSlotTime>
                {formatKickoffReadable(slot.date, slot.time)}
                <BoardPlayerMeta>
                  {t('phase2.playerAvailability.availabilityBoardOverlap', {
                    count: free.size,
                    total,
                  })}
                </BoardPlayerMeta>
              </BoardSlotTime>
              <BoardDots>
                {players.map((p) => {
                  const isFree = free.has(String(p.player_id));
                  const initial = (p.name || '?').trim().slice(0, 1).toUpperCase();
                  return (
                    <BoardDot key={p.player_id} $free={isFree} title={`${p.name}${isFree ? '' : ' — not free'}`}>
                      {initial}
                    </BoardDot>
                  );
                })}
              </BoardDots>
              {rosterOkUi && viewerPlayerId ? (
                <BoardPropose
                  type="button"
                  disabled={busy}
                  onClick={() => onVote(slot.date, slot.time, slot.slotKey, false)}
                >
                  {busy ? '…' : voted ? t('phase2.playerAvailability.availabilityBoardYourVote') : t('phase2.playerAvailability.availabilityBoardPropose')}
                </BoardPropose>
              ) : null}
            </BoardSlot>
          );
        })
      )}
    </BoardShell>
  );
}

function ViewerTeamRosterBlock({ team, t, availabilityHint }) {
  if (!team || !Array.isArray(team.players) || team.players.length === 0) return null;
  const hintText =
    availabilityHint === false || availabilityHint === null
      ? null
      : availabilityHint ?? t('phase2.playerAvailability.yourRosterAvailability');
  return (
    <ViewerRosterShell>
      <ViewerRosterHeading>{team.team_name || 'Your team'}</ViewerRosterHeading>
      {hintText ? <ViewerRosterHint>{hintText}</ViewerRosterHint> : null}
      <ViewerMemberGrid>
        {team.players.map((p) => {
          const pic = p.profile_picture_url ? publicAssetUrl(p.profile_picture_url) : '';
          return (
            <ViewerMemberCard key={p.player_id}>
              <ViewerMemberTop>
                {pic ? (
                  <ViewerAvatar src={pic} alt="" />
                ) : (
                  <ViewerAvatarPh aria-hidden="true">
                    {(p.name || '?').slice(0, 1).toUpperCase()}
                  </ViewerAvatarPh>
                )}
                <ViewerMemberName>{p.name || 'Player'}</ViewerMemberName>
              </ViewerMemberTop>
              <RosterDayList>
                {ROSTER_DAY_KEYS.map((dk) => (
                  <RosterDayRow key={dk}>
                    <RosterDayShort>{t(`phase2.playerAvailability.day_${dk}`)}</RosterDayShort>
                    <RosterDayValue>{labelForDayPref(p.time_preferences_by_day, dk, t)}</RosterDayValue>
                  </RosterDayRow>
                ))}
              </RosterDayList>
            </ViewerMemberCard>
          );
        })}
      </ViewerMemberGrid>
    </ViewerRosterShell>
  );
}

function MatchSlotCarousel({
  slots,
  rosterN,
  kickoffVotesNeed,
  kv,
  rosterOkUi,
  viewerPlayerId,
  kickoffVoteBusyKey,
  onVote,
  t,
  carouselSubtitle,
}) {
  const trackRef = useRef(null);
  if (!Array.isArray(slots) || slots.length === 0) return null;

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('[data-carousel-card]');
    const cardW = card ? card.getBoundingClientRect().width : 220;
    el.scrollBy({ left: dir * (cardW + 10) * 1.05, behavior: 'smooth' });
  };

  return (
    <CarouselSection>
      {carouselSubtitle ? <CarouselSubtitle>{carouselSubtitle}</CarouselSubtitle> : null}
      <CarouselShell>
        <CarouselArrow type="button" aria-label="Scroll left" onClick={() => scrollBy(-1)}>
          ‹
        </CarouselArrow>
        <CarouselTrack ref={trackRef}>
          {slots.map((s) => {
            const vc = kickoffVoteTally(kv, s.slotKey);
            const need =
              kickoffVotesNeed ||
              (kv?.votes_required && kv.votes_required > 0 ? kv.votes_required : rosterN);
            const mine = playerHasKickoffVote(kv, s.slotKey);
            const busy = kickoffVoteBusyKey === s.slotKey;
            const label = busy ? '…' : mine ? 'Your vote ✓' : 'Vote for time';
            return (
              <CarouselCard key={s.slotKey} data-carousel-card>
                {isSlotPeriodBlocked(s) ? (
                  <PreferredPill style={slotPeriodPillStyle(s)}>
                    {t(slotPeriodBadgeKey(s))}
                  </PreferredPill>
                ) : s.preference_aligned ? (
                  <PreferredPill>{t('phase2.playerAvailability.preferenceBadge')}</PreferredPill>
                ) : null}
                <CarouselCardDate>{formatSchedulingDateCapsLine(s.date)}</CarouselCardDate>
                <CarouselCardTime>{formatKickoffReadable(s.date, s.time)}</CarouselCardTime>
                <CarouselCardMeta>
                  {slotFitLabel(s, rosterN, t)} ·{' '}
                  {t('phase2.playerAvailability.kickoffSlotPickCount', { count: vc, need })}
                </CarouselCardMeta>
                {rosterOkUi && viewerPlayerId ? (
                  <CarouselCardVote
                    type="button"
                    disabled={busy || isSlotPeriodBlocked(s)}
                    onClick={() => onVote(s.date, s.time, s.slotKey, isSlotPeriodBlocked(s))}
                  >
                    {isSlotPeriodBlocked(s) ? t(slotPeriodCannotVoteKey(s)) : label}
                  </CarouselCardVote>
                ) : (
                  <DrawerHint $inline>Roster sign-in required</DrawerHint>
                )}
              </CarouselCard>
            );
          })}
        </CarouselTrack>
        <CarouselArrow type="button" aria-label="Scroll right" onClick={() => scrollBy(1)}>
          ›
        </CarouselArrow>
      </CarouselShell>
    </CarouselSection>
  );
}

export default MatchSchedulingPanel;
